import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useState, type CSSProperties, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getOptimizedImageUrl } from "@/components/_helpers";
import { EASE, HAIRLINE_SOFT } from "@/components/merch/merchTheme";
import useCoverTint from "@/hooks/useCoverTint";
import { usePrefersReducedMotion, useTilt } from "@/hooks/useMotionPreferences";
import { RELEASE_KIND_LABELS, releaseKind } from "@/utils/musicReleaseTabs";
import { releaseYear } from "@/utils/releaseGrid";
import { sharedProps } from "@/utils/sharedElements";
import NowPlayingBars from "./NowPlayingBars";
import { fadeInImageSx, shimmerSx } from "./shimmer";
import VinylCover, { VINYL_HOST_CLASS } from "./VinylCover";

export interface BentoRelease {
  id?: string;
  name: string;
  release_date?: string;
  total_tracks?: number;
  album_group?: string;
  images?: Array<{ url: string }>;
}

export interface BentoLayout {
  columns: number;
  small: number;
  showMore: boolean;
}

export function bentoLayout(count: number): BentoLayout {
  if (count <= 0) return { columns: 0, small: 0, showMore: false };
  if (count === 1) return { columns: 2, small: 0, showMore: false };
  if (count === 2) return { columns: 3, small: 1, showMore: true };
  if (count === 3) return { columns: 3, small: 2, showMore: false };
  if (count === 5) return { columns: 4, small: 4, showMore: false };
  return { columns: 4, small: 3, showMore: true };
}

interface ReleaseBentoProps {
  releases: BentoRelease[];
  pathFor: (release: BentoRelease) => string;
  allHref: string;
  allLabel: string;
  isPlaying: (release: BentoRelease) => boolean;
  onOpen: (release: BentoRelease, href: string) => void;
  onOpenAll: () => void;
  emptyLabel: string;
}

const coverSet = (url: string | undefined, widths: number[]) =>
  url ? widths.map((width) => `${getOptimizedImageUrl(url, { width, height: width, fit: "cover", quality: 80 })} ${width}w`).join(", ") : undefined;

const metaFor = (release: BentoRelease, withTracks = false) => {
  const kind = RELEASE_KIND_LABELS[releaseKind(release)].one;
  const year = releaseYear(release.release_date);
  const tracks = withTracks && release.total_tracks ? `${release.total_tracks} ${release.total_tracks === 1 ? "track" : "tracks"}` : "";
  return [kind, year, tracks].filter(Boolean).join(" · ");
};

function Reveal({ index, children, style }: { index: number; children: ReactNode; style?: CSSProperties }) {
  const reduced = usePrefersReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 26, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "0px 0px 160px 0px" }}
      transition={{ duration: 0.55, delay: reduced ? 0 : index * 0.07, ease: [0.23, 1, 0.32, 1] }}
      style={{ minWidth: 0, ...style }}
    >
      {children}
    </motion.div>
  );
}

function FeatureTile({ release, href, playing, onOpen }: { release: BentoRelease; href: string; playing: boolean; onOpen: () => void }) {
  const [ready, setReady] = useState(false);
  const source = release.images?.[0]?.url;
  const tint = useCoverTint(source, ready);
  const { ref, handlers } = useTilt<HTMLAnchorElement>(4);
  const kind = releaseKind(release);

  return (
    <Box
      component={RouterLink}
      to={href}
      viewTransition
      onClick={onOpen}
      ref={ref}
      {...handlers}
      className={`album ${VINYL_HOST_CLASS}`}
      data-playing={playing ? "true" : "false"}
      draggable={false}
      sx={{
        position: "relative",
        height: "100%",
        display: "grid",
        gridTemplateRows: "minmax(0, 1fr) auto",
        gap: "14px",
        p: "16px",
        borderRadius: "20px",
        overflow: "hidden",
        border: HAIRLINE_SOFT,
        color: "inherit",
        textDecoration: "none",
        background: tint
          ? `radial-gradient(120% 90% at 20% 10%, color-mix(in srgb, ${tint.glow} 34%, transparent), transparent 60%), linear-gradient(160deg, ${tint.surface}, color-mix(in srgb, var(--color1, #111) 60%, black))`
          : "color-mix(in srgb, var(--color1, #111) 70%, black)",
        boxShadow: tint ? `0 30px 60px -34px color-mix(in srgb, ${tint.glow} 80%, transparent)` : "0 20px 40px -24px rgba(0,0,0,0.6)",
        transform: "perspective(1000px) rotateX(var(--tilt-y, 0deg)) rotateY(var(--tilt-x, 0deg))",
        transition: `transform 0.5s ${EASE}, box-shadow 0.6s ${EASE}`,
        "&[data-tilting='true']": { transition: "transform 0.12s linear" },
        "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px" },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box className="bento-feature-art" sx={{ height: "100%", minHeight: 0, aspectRatio: "1 / 1", maxWidth: "81%" }}>
        <VinylCover
          src={getOptimizedImageUrl(source, { width: 480, height: 480, fit: "cover", quality: 82 })}
          srcSet={coverSet(source, [320, 480, 640])}
          sizes="(min-width: 900px) 365px, 60vw"
          alt={release.name}
          playing={playing}
          tint={tint}
          peek="always"
          shift="26%"
          priority
          sharedKey={release.id || release.name}
          onImageLoad={() => setReady(true)}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px" }}>
          <Typography component="span" sx={eyebrowSx}>
            Latest {kind === "ep" ? "EP" : RELEASE_KIND_LABELS[kind].one.toLowerCase()}
          </Typography>
          {playing ? <NowPlayingBars playing size={13} color="var(--logoColor2)" label="Now playing" /> : null}
        </Box>
        <Typography
          component="h3"
          {...sharedProps("title", release.id, release.name)}
          sx={{
            m: 0,
            width: "fit-content",
            maxWidth: "100%",
            color: "var(--logoColor2)",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 700,
            fontSize: "clamp(18px, 1.35rem, 26px)",
            lineHeight: 1.15,
            textWrap: "balance",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {release.name}
        </Typography>
        <Typography sx={{ mt: "4px", color: "var(--logoColor)", opacity: 0.8, fontFamily: "Poppins, sans-serif", fontSize: "clamp(12px, 0.82rem, 14px)" }}>
          {metaFor(release, true)}
        </Typography>
      </Box>
    </Box>
  );
}

function SmallTile({ release, href, playing, onOpen }: { release: BentoRelease; href: string; playing: boolean; onOpen: () => void }) {
  const [loaded, setLoaded] = useState(false);
  const source = release.images?.[0]?.url;
  return (
    <Box
      component={RouterLink}
      to={href}
      viewTransition
      onClick={onOpen}
      className="album"
      data-playing={playing ? "true" : "false"}
      draggable={false}
      sx={{
        position: "relative",
        display: "block",
        aspectRatio: "1 / 1",
        borderRadius: "16px",
        overflow: "hidden",
        border: HAIRLINE_SOFT,
        color: "#fff",
        textDecoration: "none",
        backgroundColor: "rgba(0,0,0,0.3)",
        boxShadow: "0 14px 30px -18px rgba(0,0,0,0.7)",
        "&[data-playing='true']": { boxShadow: "0 0 0 2px var(--logoColor2), 0 14px 30px -18px rgba(0,0,0,0.7)" },
        "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px" },
        "@media (hover: hover)": {
          "&:hover img": { transform: "scale(1.06)" },
          "&:hover .bento-caption": { transform: "translateY(-3px)" },
        },
      }}
    >
      <Box
        data-loaded={loaded ? "true" : "false"}
        {...sharedProps("art", release.id, release.name)}
        sx={{ ...shimmerSx, position: "absolute", inset: 0, borderRadius: "inherit", overflow: "hidden" }}
      >
        <Box
          component="img"
          src={getOptimizedImageUrl(source, { width: 360, height: 360, fit: "cover", quality: 80 })}
          srcSet={coverSet(source, [240, 360, 480])}
          sizes="(min-width: 900px) 200px, 45vw"
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          sx={{
            ...fadeInImageSx,
            position: "relative",
            zIndex: 1,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            transition: `opacity 0.35s ease, transform 0.7s ${EASE}`,
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />
      </Box>
      <Box aria-hidden sx={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(180deg, transparent 42%, rgba(0,0,0,0.82) 100%)" }} />
      <Box className="bento-caption" sx={{ position: "absolute", left: "12px", right: "12px", bottom: "10px", zIndex: 2, transition: `transform 0.4s ${EASE}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {playing ? <NowPlayingBars playing size={12} color="#fff" label="Now playing" /> : null}
          <Typography
            component="h3"
            {...sharedProps("title", release.id, release.name)}
            sx={{
              m: 0,
              minWidth: 0,
              maxWidth: "100%",
              color: "#fff",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: "clamp(13px, 0.9rem, 16px)",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {release.name}
          </Typography>
        </Box>
        <Typography sx={{ mt: "2px", color: "rgba(255,255,255,0.78)", fontFamily: "Poppins, sans-serif", fontSize: "clamp(11px, 0.75rem, 13px)" }}>
          {metaFor(release)}
        </Typography>
      </Box>
    </Box>
  );
}

function MoreTile({ releases, href, label, onOpen }: { releases: BentoRelease[]; href: string; label: string; onOpen: () => void }) {
  const stack = releases.slice(0, 3);
  return (
    <Box
      component={RouterLink}
      to={href}
      viewTransition
      onClick={onOpen}
      sx={{
        position: "relative",
        aspectRatio: "1 / 1",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: "14px",
        borderRadius: "16px",
        border: "1px dashed color-mix(in srgb, var(--logoColor) 40%, transparent)",
        backgroundColor: "color-mix(in srgb, var(--color1, #111) 45%, transparent)",
        color: "var(--logoColor2)",
        textDecoration: "none",
        transition: `border-color 0.3s ${EASE}, background-color 0.3s ${EASE}`,
        "&:hover": { borderColor: "var(--logoColor2)", backgroundColor: "color-mix(in srgb, var(--color1, #111) 70%, transparent)" },
        "&:hover .more-stack > *": { transform: "translateX(calc(var(--i) * 10px)) rotate(calc(var(--i) * 4deg))" },
        "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px" },
      }}
    >
      <Box className="more-stack" aria-hidden sx={{ position: "relative", height: "46%" }}>
        {stack.map((release, index) => (
          <Box
            key={release.id || release.name}
            component="img"
            src={getOptimizedImageUrl(release.images?.[0]?.url, { width: 120, height: 120, fit: "cover", quality: 70 })}
            alt=""
            loading="lazy"
            style={{ ["--i" as string]: index } as CSSProperties}
            sx={{
              position: "absolute",
              left: `${index * 18}%`,
              top: 0,
              height: "100%",
              aspectRatio: "1 / 1",
              objectFit: "cover",
              borderRadius: "8px",
              boxShadow: "0 8px 18px rgba(0,0,0,0.45)",
              zIndex: 3 - index,
              transition: `transform 0.5s ${EASE}`,
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
        <Typography sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "clamp(13px, 0.95rem, 17px)", lineHeight: 1.2 }}>{label}</Typography>
        <Box component={ArrowUpRight} aria-hidden sx={{ width: "20px", height: "20px", flexShrink: 0 }} />
      </Box>
    </Box>
  );
}

const eyebrowSx = {
  color: "var(--logoColor)",
  opacity: 0.85,
  fontFamily: "Poppins, sans-serif",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
};

export function ReleaseBento({ releases, pathFor, allHref, allLabel, isPlaying, onOpen, onOpenAll, emptyLabel }: ReleaseBentoProps) {
  if (releases.length === 0) {
    return (
      <Typography className="no-albums" sx={{ color: "var(--logoColor2)", fontFamily: "Poppins, sans-serif", fontSize: { xs: "2rem", md: "1.2rem" }, py: "2rem" }}>
        {emptyLabel}
      </Typography>
    );
  }

  const layout = bentoLayout(releases.length);
  const [feature, ...rest] = releases;
  const smalls = rest.slice(0, layout.small);
  const remaining = releases.slice(1 + layout.small);
  const featureHref = pathFor(feature);

  return (
    <Box
      className="release-bento"
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
        gridAutoRows: "auto",
        gap: "16px",
        maxWidth: layout.columns === 2 ? "440px" : "none",
      }}
    >
      <Reveal index={0} style={{ gridColumn: "span 2", gridRow: layout.small > 0 || layout.showMore ? "span 2" : "auto", aspectRatio: layout.small > 0 || layout.showMore ? "auto" : "1 / 1", height: "100%" }}>
        <FeatureTile release={feature} href={featureHref} playing={isPlaying(feature)} onOpen={() => onOpen(feature, featureHref)} />
      </Reveal>
      {smalls.map((release, index) => {
        const href = pathFor(release);
        return (
          <Reveal key={release.id || release.name} index={index + 1}>
            <SmallTile release={release} href={href} playing={isPlaying(release)} onOpen={() => onOpen(release, href)} />
          </Reveal>
        );
      })}
      {layout.showMore ? (
        <Reveal index={smalls.length + 1}>
          <MoreTile releases={remaining.length > 0 ? remaining : releases} href={allHref} label={remaining.length > 0 ? `${remaining.length} more ${allLabel}` : `Browse all ${allLabel}`} onOpen={onOpenAll} />
        </Reveal>
      ) : null}
    </Box>
  );
}

export default ReleaseBento;
